import { Navigate, Outlet } from "@tanstack/react-router";
import { AlertTriangle, LoaderCircle } from "lucide-react";

import { useAuth } from "../lib/auth-context";
import { LogoMark } from "./logo-mark";
import { Button } from "./ui/button";

function FullPageLoading({ label }: { label: string }) {
  return (
    <div className="grid min-h-screen place-items-center px-6">
      <div className="text-center">
        <LogoMark />
        <LoaderCircle
          aria-label={label}
          className="mx-auto mt-8 size-5 animate-spin text-ink-soft"
        />
      </div>
    </div>
  );
}

function SessionLoading() {
  return <FullPageLoading label="Checking your session" />;
}

export function RouteLoading() {
  return <FullPageLoading label="Loading Tax2 page" />;
}

function SessionUnavailable() {
  const { initializationError, refreshSession } = useAuth();

  return (
    <div className="grid min-h-screen place-items-center px-5 py-10">
      <section className="w-full max-w-md rounded-3xl border border-line bg-surface p-7 text-center shadow-soft sm:p-9">
        <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-warning-soft text-warning">
          <AlertTriangle aria-hidden="true" className="size-5" />
        </span>
        <h1 className="mt-5 text-2xl font-black tracking-[-0.04em] text-ink">
          Tax2 is temporarily unavailable
        </h1>
        <p className="mt-3 text-sm leading-6 text-ink-soft">
          {initializationError ?? "The session service did not respond."}
        </p>
        <Button className="mt-6" onClick={() => void refreshSession()}>
          Try again
        </Button>
      </section>
    </div>
  );
}

export function AuthenticatedRoute() {
  const { status } = useAuth();

  if (status === "loading") {
    return <SessionLoading />;
  }
  if (status === "unavailable") {
    return <SessionUnavailable />;
  }
  if (status === "guest") {
    return <Navigate replace to="/login" />;
  }

  return <Outlet />;
}

export function PublicRoute() {
  const { status } = useAuth();

  if (status === "loading") {
    return <SessionLoading />;
  }
  if (status === "authenticated") {
    return <Navigate replace to="/" />;
  }

  return <Outlet />;
}
